import type { LOANREQUEST_STATUS } from 'cpool-constants';
import {
    LOAN_REQUEST_ALL_BANKS,
    LOAN_REQUEST_TAGS,
    LOANREQUEST_BUCKETS,
    LOANREQUEST_BUCKETS_FILTERS,
    LOANREQUEST_DEFAULT_STATUS,
    LOANREQUEST_STATUSES,
    PARTNER_LEVELS,
    REASON_CATEGORIES,
    SUB_STATUS_APPROVED,
    SUB_STATUS_IN_PROGRESS,
    SUB_STATUS_INITIAL,
    SUB_STATUS_OFFER_AT_BANK,
    SUB_STATUS_OFFER_AT_CUSTOMER,
    SUB_STATUSES_FILTERS,
} from 'cpool-constants';
import { z } from 'zod';

import { PAGINATION } from '~/constants';

export const schemaLeadListSearch = z.object({
    adviser: z.array(z.string()).optional().default([]).catch([]),
    bank: z
        .array(z.union([z.nativeEnum(LOAN_REQUEST_ALL_BANKS), z.string()]))
        .optional()
        .default([])
        .catch([]),
    bucket: z
        .array(z.nativeEnum({ ...LOANREQUEST_BUCKETS, ...LOANREQUEST_BUCKETS_FILTERS }))
        .optional()
        .default([])
        .catch([]),
    page: z.number().optional().default(1).catch(1),
    pageSize: z
        .number()
        .refine((val) => PAGINATION.LEAD_LIST.PAGE_SIZE_OPTIONS.includes(val))
        .optional()
        .default(PAGINATION.LEAD_LIST.DEFAULT_PAGE_SIZE)
        .catch(PAGINATION.LEAD_LIST.DEFAULT_PAGE_SIZE),
    partnerLevel: z.array(z.nativeEnum(PARTNER_LEVELS)).optional().default([]).catch([]),
    resubmission: z.boolean().optional().default(false).catch(false),
    tag: z.array(z.nativeEnum(LOAN_REQUEST_TAGS)).optional().default([]).catch([]),
});

export const schemaUnionLeadListSearch = z.union([
    z
        .object({
            status: z.literal(LOANREQUEST_STATUSES.APPROVED),
            subStatus: z
                .array(z.nativeEnum({ ...SUB_STATUS_APPROVED, ...SUB_STATUSES_FILTERS }))
                .optional()
                .default([])
                .catch([]),
        })
        .merge(schemaLeadListSearch),
    z
        .object({
            status: z.literal(LOANREQUEST_STATUSES.IN_PROGRESS),
            subStatus: z
                .array(z.nativeEnum({ ...SUB_STATUS_IN_PROGRESS, ...SUB_STATUSES_FILTERS }))
                .optional()
                .default([])
                .catch([]),
        })
        .merge(schemaLeadListSearch),
    z
        .object({
            status: z.literal(LOANREQUEST_STATUSES.INITIAL),
            subStatus: z
                .array(z.nativeEnum({ ...SUB_STATUS_INITIAL, ...SUB_STATUSES_FILTERS }))
                .optional()
                .default([])
                .catch([]),
        })
        .merge(schemaLeadListSearch),
    z
        .object({
            status: z.literal(LOANREQUEST_STATUSES.OFFER_AT_CUSTOMER),
            subStatus: z
                .array(z.nativeEnum({ ...SUB_STATUS_OFFER_AT_CUSTOMER, ...SUB_STATUSES_FILTERS }))
                .optional()
                .default([])
                .catch([]),
        })
        .merge(schemaLeadListSearch),
    z
        .object({
            status: z.literal(LOANREQUEST_STATUSES.CANCELED),
            subStatus: z
                .array(z.nativeEnum({ ...REASON_CATEGORIES, ...SUB_STATUSES_FILTERS }))
                .optional()
                .default([])
                .catch([]),
        })
        .merge(schemaLeadListSearch),
    z
        .object({
            status: z.literal(LOANREQUEST_STATUSES.OFFER_AT_BANK),
            subStatus: z
                .array(z.nativeEnum({ ...SUB_STATUS_OFFER_AT_BANK, ...SUB_STATUSES_FILTERS }))
                .optional()
                .default([])
                .catch([]),
        })
        .merge(schemaLeadListSearch),
    z
        .object({
            status: z.literal(LOANREQUEST_DEFAULT_STATUS.ACTIVE),
            subStatus: z.array(z.string()).optional().default([]).catch([]),
        })
        .merge(schemaLeadListSearch),
    z
        .object({
            status: z
                .literal(LOANREQUEST_DEFAULT_STATUS.ALL)
                .optional()
                .default(LOANREQUEST_DEFAULT_STATUS.ALL)
                .catch(LOANREQUEST_DEFAULT_STATUS.ALL),
            subStatus: z.array(z.string()).optional().default([]).catch([]),
        })
        .merge(schemaLeadListSearch),
]);

export type SchemaLeadListSearch = z.infer<typeof schemaUnionLeadListSearch>;
export type SchemaLeadListSearchKeys = keyof SchemaLeadListSearch;

export type LeadListFilter = {
    bucket: Array<keyof typeof LOANREQUEST_BUCKETS | keyof typeof LOANREQUEST_BUCKETS_FILTERS>;
    status: LOANREQUEST_STATUS | LOANREQUEST_DEFAULT_STATUS;
    adviser: string[];
    bank: Array<(keyof typeof LOAN_REQUEST_ALL_BANKS)[number] | string>;
    page: number;
    pageSize: number;
    partnerLevel: Array<(typeof PARTNER_LEVELS)[keyof typeof PARTNER_LEVELS]>;
    resubmission: boolean;
    subStatus: string[];
    tag: string[];
};
